// A library that looks different today than it did yesterday.
//
// THE PROBLEM: the grid sorts by sort_order then created_at, identically
// for everyone, every time. With seven hundred published drills that
// means the same two dozen cards ARE the library and the rest is stock
// nobody has seen. Re-ordering is the cheapest way to put the back of
// the catalogue in front of people.
//
// WHY A DAILY SEED RATHER THAN Math.random():
//
// Random per render is unusable. The order would change when a coach
// toggles a filter, opens a drill and comes back, or the component
// re-renders for any of a dozen reasons. That isn't discovery, it's the
// floor moving.
//
// Random per visit is better but still costs more than it gives: a
// coach who finds a drill in the morning cannot find it again in the
// afternoon. A library you can't return to isn't a library.
//
// So the seed is the date. The order holds all day, across tabs and
// across devices, which is long enough that "it was near the top, left
// hand side" still works - and short enough that the catalogue rotates
// fully through the top of the grid inside a week.
//
// WHAT IS NOT SHUFFLED, and why, is decided at the call site: search
// results (relevance is the answer to a search), New This Week
// (chronological is the entire point of it), and the Longer videos
// shelf. This module only knows how to produce an order.

export function dailySeed(now = new Date()) {
  // Local date, not UTC: the day should turn over when it turns over
  // for the coach, not at 5pm the evening before.
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// FNV-1a. Small, fast, and much better distributed across short similar
// strings (which ids are) than the classic `hash*31 + c`, where near
// neighbours land near each other and the shuffle visibly clumps.
export function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * shuffleDaily — a stable pseudo-random order for today.
 *
 * Curation wins over rotation: a drill with an explicit sort_order was
 * deliberately placed by a human, so it keeps its place at the top and
 * only the unplaced remainder rotates. sort_order is 0 for almost
 * everything, which is what makes it a usable signal - it means
 * "somebody chose this" rather than "this row exists".
 *
 * Pure. Same input and same day, same output, however many times it is
 * called during a render.
 */
export function shuffleDaily(items, seed = dailySeed()) {
  if (!Array.isArray(items) || items.length < 2) return items || [];
  return [...items].sort((a, b) => {
    const ao = Number(a.sortOrder || 0);
    const bo = Number(b.sortOrder || 0);
    const aPinned = ao > 0;
    const bPinned = bo > 0;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (aPinned && ao !== bo) return ao - bo;
    return hashString(seed + String(a.id)) - hashString(seed + String(b.id));
  });
}
